import React, { useEffect, useState } from "react";
import "./ProfileTable.css";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  Modal,
  TextField,
  MenuItem,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";

const months = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const ProfileTable = () => {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [viewData, setViewData] = useState(null);

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");

  /* ======================
     FETCH PROFILE DATA
  ====================== */
  useEffect(() => {
    fetch("https://dummyjson.com/users")
      .then((res) => {
        if (!res.ok) throw new Error("Fetch failed");
        return res.json();
      })
      .then((res) => {
        const mapped = res.users.slice(0, 10).map((u, i) => ({
          id: u.id,
          jobTitle: "Software Engineer",
          candidateId: `EMP-${1000 + i}`,
          candidateName: `${u.firstName} ${u.lastName}`,
          contactNumber: u.phone,
          emailId: u.email,
          serviceCommitment: 2,
          serviceBreakAmount: 50000,
          ctc: 600000,
          status: "Active",
          joiningDate: "2025-02-10",
        }));
        setData(mapped);
      })
      .catch(() => setError(true));
  }, []);

  /* ======================
     FILTER LOGIC
  ====================== */
  const filteredData = data.filter((row) => {
    if (!row.joiningDate) return true;
    const d = new Date(row.joiningDate);
    const yMatch = year ? d.getFullYear().toString() === year : true;
    const mMatch = month ? d.getMonth() === Number(month) : true;
    return yMatch && mMatch;
  });

  /* ======================
     EXPORT CSV
  ====================== */
  const handleExport = () => {
    const header = Object.keys(data[0]).join(",");
    const rows = data.map((row) => Object.values(row).join(","));
    const csv = [header, ...rows].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "employee_profiles.csv";
    a.click();
  };

  /* ======================
     IMPORT CSV
  ====================== */
  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const lines = evt.target.result.split("\n");
      const headers = lines[0].split(",");

      const rows = lines.slice(1).map((line, i) => {
        const values = line.split(",");
        let obj = { id: Date.now() + i };
        headers.forEach((h, index) => {
          obj[h.trim()] = values[index];
        });
        return obj;
      });

      setData((prev) => [...prev, ...rows]);
    };
    reader.readAsText(file);
  };

  return (
    <Box className="profile-wrapper">

      {/* HEADER */}
      <Box className="profile-header">
        <h2>Employee Profile</h2>

        <div className="header-controls">
          {/* LEFT FILTERS */}
          <div className="filters">
            <TextField
              select
              size="small"
              label="Year"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {[2024, 2025, 2026].map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {months.map((m, i) => (
                <MenuItem key={i} value={i}>{m}</MenuItem>
              ))}
            </TextField>
          </div>

          {/* RIGHT ACTIONS */}
          <div className="header-actions">
            <Button component="label" startIcon={<UploadFileIcon />}>
              Import
              <input hidden type="file" accept=".csv" onChange={handleImport} />
            </Button>

            <Button startIcon={<DownloadIcon />} onClick={handleExport}>
              Export
            </Button>
          </div>
        </div>
      </Box>

      {/* TABLE */}
      <Paper className="profile-table-wrapper">
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>S.NO</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Candidate Id</TableCell>
              <TableCell>Candidate Name</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Commitment</TableCell>
              <TableCell>Break Amount</TableCell>
              <TableCell>CTC</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {error ? (
              <TableRow>
                <TableCell colSpan={11} align="center" className="error-text">
                  Failed to fetch data
                </TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No records found
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row, index) => (
                <TableRow key={row.id}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{row.jobTitle}</TableCell>
                  <TableCell>{row.candidateId}</TableCell>
                  <TableCell>{row.candidateName}</TableCell>
                  <TableCell>{row.contactNumber}</TableCell>
                  <TableCell>{row.emailId}</TableCell>
                  <TableCell>{row.serviceCommitment}</TableCell>
                  <TableCell>₹ {row.serviceBreakAmount}</TableCell>
                  <TableCell>₹ {row.ctc}</TableCell>
                  <TableCell>
                    <span className="status active">{row.status}</span>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => setViewData(row)}>
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* VIEW MODAL */}
      <Modal open={!!viewData} onClose={() => setViewData(null)}>
        <Box className="profile-modal">
          <div className="modal-header">
            <h3>Profile Details</h3>
            <IconButton onClick={() => setViewData(null)}>
              <CloseIcon />
            </IconButton>
          </div>

          {viewData && (
            <div className="view-grid">
              {Object.entries(viewData).map(([k, v]) =>
                k !== "id" ? (
                  <div key={k}>
                    <span>{k.replace(/([A-Z])/g, " $1")}</span>
                    <p>{v}</p>
                  </div>
                ) : null
              )}
            </div>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default ProfileTable;
